import { InvestigationTrendPoint } from "../../lusca-search-api/generated-src";

/** Window the "growing" signals compare over (matches the backend's growth7d). */
export const GROWTH_WINDOW_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Snapshot time of a point; NaN when unparseable. */
export const pointTime = (p: InvestigationTrendPoint): number =>
  new Date(p.t).getTime();

/**
 * The size a trend tracks: rule matches in the DLQ (`live`) when recorded, else `n`. For an
 * investigation `n` is only what it owns, which drops when items move to a split-off child or
 * another case, even though the issue itself did not shrink. Backlog groups only have `n`.
 */
export const pointValue = (p: InvestigationTrendPoint): number =>
  typeof p.live === "number" ? p.live : p.n;

/** Well-formed points, oldest first. */
export function cleanTrend(
  points: InvestigationTrendPoint[] | undefined | null
): InvestigationTrendPoint[] {
  return (points ?? [])
    .filter((p) => p && typeof p.n === "number" && !Number.isNaN(pointTime(p)))
    .sort((a, b) => pointTime(a) - pointTime(b));
}

/** How far before the window start a point may lie and still anchor it (history is daily). */
const ANCHOR_SLACK_MS = DAY_MS;

/**
 * Points within `days` of the newest one (windows follow snapshot time, not the wall clock),
 * preceded by the anchor: the last point at or up to a day before the window start, i.e. the
 * size "7 days ago". Without it the baseline is whichever snapshot happens to fall first inside
 * the window, so a one-snapshot dip there read as growth once the count recovered.
 */
export function recentTrend(
  points: InvestigationTrendPoint[] | undefined | null,
  days = GROWTH_WINDOW_DAYS
): InvestigationTrendPoint[] {
  const series = cleanTrend(points);
  if (!series.length) return series;
  const since = pointTime(series[series.length - 1]) - days * DAY_MS;
  const window = series.filter((p) => pointTime(p) >= since);
  const before = series.filter(
    (p) => pointTime(p) < since && pointTime(p) >= since - ANCHOR_SLACK_MS
  );
  return before.length ? [before[before.length - 1], ...window] : window;
}

/**
 * Latest count minus the count 7 days ago (the anchor, else the earliest point in the window);
 * null with fewer than two points (a population seen once has no trend yet). Mirrors
 * investigation_trends.growth.
 */
export function trendGrowth(
  points: InvestigationTrendPoint[] | undefined | null,
  days = GROWTH_WINDOW_DAYS
): number | null {
  const window = recentTrend(points, days);
  if (window.length < 2) return null;
  return pointValue(window[window.length - 1]) - pointValue(window[0]);
}

/** "+280", "−40", "0"; "—" when there is no trend yet. */
export function formatGrowth(growth: number | null | undefined): string {
  if (growth === null || growth === undefined) return "—";
  if (growth > 0) return `+${growth.toLocaleString()}`;
  if (growth < 0) return `−${Math.abs(growth).toLocaleString()}`;
  return "0";
}

/** Plain-language summary for screen readers and tooltips. */
export function describeTrend(
  points: InvestigationTrendPoint[] | undefined | null,
  days = GROWTH_WINDOW_DAYS
): string {
  const window = recentTrend(points, days);
  if (window.length < 2) return "Not enough snapshots for a trend yet";
  const first = pointValue(window[0]);
  const last = pointValue(window[window.length - 1]);
  const over = `over ${window.length} snapshots`;
  if (last === first)
    return `Unchanged at ${last.toLocaleString()} items ${over}`;
  const verb = last > first ? "Grew" : "Shrank";
  return `${verb} from ${first.toLocaleString()} to ${last.toLocaleString()} items ${over}`;
}

/** True when any point in the series is a group-level estimate (oversized case). */
export const hasEstimates = (
  points: InvestigationTrendPoint[] | undefined | null
): boolean => cleanTrend(points).some((p) => p.est);
